import React, { Fragment, useState } from 'react';
import PropTypes from 'prop-types';
import { map, keys, pickBy } from 'lodash-es';
import { Button } from 'reactstrap';
import classNames from 'classnames';
// Models
import { InventoryEntities } from 'client/data/models/inventory';
// Utils
import { isUsed } from 'site-modules/shared/utils/inventory-utils/is-used';
import { getTopFeatures } from 'client/site-modules/shared/utils/inventory/features-and-specs';
import { isPartnerDealer } from 'site-modules/shared/utils/dealer-details-utils';
// Constants
import { TrackingConstant } from 'client/tracking/constant';
import { INSTALLED_UPGRADES } from 'client/site-modules/shared/constants/inventory/features-and-specs';
// Components
import { ContentSection } from 'site-modules/shared/components/content-section/content-section';
import { FeatureItem } from 'site-modules/inventory/components/features-and-specs/feature-item';
import { VdpModule } from 'site-modules/shared/components/inventory/vdp-module/vdp-module';
import { InstalledUpgrades } from 'site-modules/inventory/components/features-and-specs/installed-upgrades';
import { FeaturesModal } from 'site-modules/inventory/components/features-modal/features-modal';

import './features-and-specs-base.scss';

/**
 * Component that represents all options, features and specs with
 * show/hide functionality.
 */
export function FeaturesAndSpecsBase({
  vinData = {},
  isMobile = false,
  featuresByCategories = {},
  hideFeatures = false,
  showInstalledUpgrades = false,
}) {
  const [isModalOpen, setModal] = useState(false);
  const [defaultDisplay, setDefaultDisplay] = useState(undefined);
  const isPartnerListing = isPartnerDealer(vinData);
  const isUsedType = isUsed(vinData.type);

  function toggleModal() {
    if (isModalOpen) {
      setDefaultDisplay(undefined);
    }

    setModal(!isModalOpen);
  }

  function toggleModalIU() {
    if (!isModalOpen) {
      setDefaultDisplay(INSTALLED_UPGRADES);
    }

    toggleModal();
  }

  const topFeatures = getTopFeatures(vinData);

  const seeAllMap = {
    features: !hideFeatures,
    upgrades: showInstalledUpgrades,
  };

  const featuresList = (
    <Fragment>
      <div className={classNames('features-content', { 'mb-1_5': !showInstalledUpgrades })}>
        {map(featuresByCategories, (features, categoryTitle) => (
          <FeatureItem key={categoryTitle} id={categoryTitle} name={categoryTitle} features={features} />
        ))}
        {showInstalledUpgrades && (
          <InstalledUpgrades vinData={vinData} isExpanded={hideFeatures} toggleModal={toggleModalIU} />
        )}
      </div>
      <Button
        className={classNames('modal-open-button size-16', { 'w-100': isMobile })}
        size="sm"
        color="outline-blue-50"
        data-tracking-id="vdp_view_content"
        data-subaction-name={TrackingConstant.VIEW_FEATURES}
        onClick={toggleModal}
        aria-haspopup="dialog"
      >
        All {keys(pickBy(seeAllMap)).join(' & ')}
      </Button>
      <FeaturesModal
        vinData={vinData}
        isModalOpen={isModalOpen}
        toggleModal={toggleModal}
        defaultDisplay={defaultDisplay}
      />
    </Fragment>
  );
  return (
    <ContentSection
      className="features-and-specs-base"
      {...(hideFeatures ? {} : { dataTrackingParent: 'edm-entry-features-specs' })}
      titleClassName="section-title heading-4"
      {...(isUsedType || isPartnerListing ? { headerTag: 'h2' } : {})}
      line={false}
    >
      {!hideFeatures && !!topFeatures.length && (
        <Fragment>
          <ul className="list-unstyled top-features mt-1 d-flex flex-wrap mb-1">
            {topFeatures.map(({ optionText }) => (
              <li
                key={optionText}
                className="top-feature overlay-white-60 d-inline-block medium text-gray-darker fw-medium px-0_75 me-0_5 mb-0_5"
              >
                <span>{optionText}</span>
              </li>
            ))}
            <li className="more-btn d-flex">
              <button
                className="text-underline d-inline-block medium text-gray-darker fw-medium mb-0_25 border-0 c-pointer"
                onClick={toggleModal}
                data-tracking-id="vdp_view_content"
                data-subaction-name={TrackingConstant.VIEW_FEATURES}
                aria-haspopup="dialog"
              >
                + more
                <span className="visually-hidden">{keys(pickBy(seeAllMap)).join(' & ')}</span>
              </button>
            </li>
          </ul>
        </Fragment>
      )}
      <VdpModule className="bg-white" indentationSize={1.5}>
        {featuresList}
      </VdpModule>
    </ContentSection>
  );
}

FeaturesAndSpecsBase.propTypes = {
  vinData: InventoryEntities.InventoryVin,
  isMobile: PropTypes.bool,
  featuresByCategories: PropTypes.shape({}),
  hideFeatures: PropTypes.bool,
  showInstalledUpgrades: PropTypes.bool,
};
